import React, { useEffect, useState, useRef } from "react";
import {
  getAllManagers,
  createManager,
  updateManager,
  deleteManager,
} from "../api/adminService";

const ManagersContent = () => {
  const [managers, setManagers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedManager, setSelectedManager] = useState(null); // For edit/delete
  const [form, setForm] = useState({ username: "", name: "", email: "" });
  const [confirmEmail, setConfirmEmail] = useState("");
  const isMounted = useRef(false);

  const fetchManagers = async (limit, lastEvaluatedKey) => {
    try {
      const response = await getAllManagers(limit, lastEvaluatedKey);
      setManagers((prevManagers) => [...prevManagers, ...response.managers]);
      setPagination(response.pagination);
    } catch (err) {
      setError("Failed to fetch managers. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!isMounted.current) {
      fetchManagers(10, null);
      isMounted.current = true;
    }
  }, []);

  const handleLoadMore = () => {
    if (pagination.lastEvaluatedKey) {
      setLoadingMore(true);
      fetchManagers(10, pagination.lastEvaluatedKey);
    }
  };

  const handleOpenModal = (manager = null, isDelete = false) => {
    setSelectedManager(manager);
    setForm(manager || { username: "", name: "", email: "" });
    setDeleteMode(isDelete);
    setShowModal(true);
    setConfirmEmail("");
    setSuccessMessage("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedManager(null);
    setDeleteMode(false);
    setConfirmEmail("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmEmailChange = (e) => {
    setConfirmEmail(e.target.value);
  };

  const handleSave = async () => {
    try {
      if (selectedManager) {
        await updateManager(selectedManager.userId, {
          name: form.name,
          email: form.email,
        });
        setManagers((prevManagers) =>
          prevManagers.map((manager) =>
            manager.userId === selectedManager.userId
              ? { ...manager, ...form }
              : manager
          )
        );
      } else {
        const newManager = await createManager(form);
        setManagers((prevManagers) => [newManager, ...prevManagers]);
        setSuccessMessage("Manager created successfully.");
      }
      handleCloseModal();
      setTimeout(() => setSuccessMessage(""), 5000); // Clear success message after 5 seconds
    } catch (err) {
      alert("Failed to save manager. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (confirmEmail !== selectedManager.email) {
      alert("Entered email does not match. Please try again.");
      return;
    }

    try {
      await deleteManager(selectedManager.userId);
      setManagers((prevManagers) =>
        prevManagers.filter((manager) => manager.userId !== selectedManager.userId)
      );
      setSuccessMessage("Manager deleted successfully.");
      handleCloseModal();
      setTimeout(() => setSuccessMessage(""), 5000); // Clear success message after 5 seconds
    } catch (err) {
      alert("Failed to delete manager. Please try again.");
    }
  };

  return (
    <div className="managers-content">
      <h2 className="text-2xl font-bold mb-4">Managers</h2>
      <p>View detailed information for managers.</p>

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      {/* Conditionally Render the Button */}
      {!loading && (
        <button
          className="btn btn-success mb-3"
          onClick={() => handleOpenModal()}
        >
          Create New Manager
        </button>
      )}

      {loading ? (
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div>
          <h3>List of Managers</h3>
          {managers.length > 0 ? (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Created At</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {managers.map((manager, index) => (
                  <tr key={manager.userId}>
                    <td>{index + 1}</td>
                    <td>{manager.userId}</td>
                    <td>{manager.email}</td>
                    <td>{manager.name}</td>
                    <td>{new Date(manager.createdAt).toLocaleString()}</td>
                    <td>{manager.isActive ? "Yes" : "No"}</td>
                    <td>
                      <button
                        className="btn btn-warning btn-sm me-2"
                        onClick={() => handleOpenModal(manager)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleOpenModal(manager, true)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No managers found.</p>
          )}
          {pagination.lastEvaluatedKey && (
            <button
              className="btn btn-primary mt-3"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          )}
        </div>
      )}

      {/* Modal for Create/Edit/Delete */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {deleteMode
                    ? "Confirm Manager Deletion"
                    : selectedManager
                    ? "Edit Manager"
                    : "Create New Manager"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                {deleteMode ? (
                  <div>
                    <p>
                      Are you sure you want to delete the manager{" "}
                      <strong>{selectedManager.name}</strong>? Please confirm their
                      email: <strong>{selectedManager.email}</strong>
                    </p>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Enter manager's email"
                      value={confirmEmail}
                      onChange={handleConfirmEmailChange}
                    />
                  </div>
                ) : (
                  <form>
                    {!selectedManager && (
                      <div className="mb-3">
                        <label className="form-label">Username</label>
                        <input
                          type="text"
                          className="form-control"
                          name="username"
                          value={form.username}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                      />
                    </div>
                  </form>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                {deleteMode ? (
                  <button className="btn btn-danger" onClick={handleDelete}>
                    Confirm Delete
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={handleSave}>
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagersContent;
